import type { Feature, GeoJsonProperties, Geometry } from "geojson";
import type { MapLocation } from "@/Shared/Maps/MapLocation";
import type { LatitudeLongitude } from "@freightsimple/generated-dashboard-openapi-client/models/LatitudeLongitude";
import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { SymbolLayer } from "mapbox-gl";
import { getMarkerName } from "./images";
import { hasValidLatitudeLongitude } from "./hasValidLatitudeLongitude";
import { latitudeLongitudeToNumbers } from "./latitudeLongitudeToNumbers";

function getFeatureForPoint(
  latitudeLongitude: Required<LatitudeLongitude>,
): Feature<Geometry, GeoJsonProperties> {
  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Point",
      coordinates: latitudeLongitudeToNumbers(latitudeLongitude),
    },
  };
}

function getLayerForPoint(
  pickupOrDelivery: "pickup" | "delivery",
  shipmentState: ShipmentState,
): SymbolLayer {
  return {
    id: pickupOrDelivery + "Point",
    source: pickupOrDelivery + "PointSource",
    type: "symbol",
    layout: {
      "icon-image": getMarkerName(pickupOrDelivery, shipmentState),
      "icon-rotate": ["get", "bearing"],
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  };
}

function addPickupMapLayer(
  map: mapboxgl.Map,
  latitudeLongitude: Required<LatitudeLongitude>,
  shipmentState: ShipmentState,
) {
  map.addSource("pickupPointSource", {
    type: "geojson",
    data: getFeatureForPoint(latitudeLongitude),
  });

  map.addLayer(getLayerForPoint("pickup", shipmentState));
}

function removePickupMapLayer(map: mapboxgl.Map) {
  if (map.getLayer("pickupPoint")) {
    map.removeLayer("pickupPoint");
  }
  if (map.getSource("pickupPointSource")) {
    map.removeSource("pickupPointSource");
  }
}

function addDeliveryMapLayer(
  map: mapboxgl.Map,
  latitudeLongitude: Required<LatitudeLongitude>,
  shipmentState: ShipmentState,
) {
  map.addSource("deliveryPointSource", {
    type: "geojson",
    data: getFeatureForPoint(latitudeLongitude),
  });

  map.addLayer(getLayerForPoint("delivery", shipmentState));
}

function removeDeliveryMapLayer(map: mapboxgl.Map) {
  if (map.getLayer("deliveryPoint")) {
    map.removeLayer("deliveryPoint");
  }
  if (map.getSource("deliveryPointSource")) {
    map.removeSource("deliveryPointSource");
  }
}

export function setPickupLocation(
  map: mapboxgl.Map,
  pickup: Partial<MapLocation> | undefined,
  shipmentState: ShipmentState,
) {
  removePickupMapLayer(map);

  // Skip the marker when the coordinate is missing or (0,0) — otherwise it
  // drops a pin in the ocean off Africa (ENG-4342).
  if (!hasValidLatitudeLongitude(pickup)) {
    return;
  }

  addPickupMapLayer(map, pickup.latitudeLongitude, shipmentState);
}

export function setDeliveryLocation(
  map: mapboxgl.Map,
  delivery: Partial<MapLocation> | undefined,
  shipmentState: ShipmentState,
) {
  removeDeliveryMapLayer(map);

  // Skip the marker when the coordinate is missing or (0,0) — otherwise it
  // drops a pin in the ocean off Africa (ENG-4342).
  if (!hasValidLatitudeLongitude(delivery)) {
    return;
  }

  addDeliveryMapLayer(map, delivery.latitudeLongitude, shipmentState);
}
